import {MipLevelRegion, Region} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {HalImage} from "@cm/hal"
import {ImageOpContextHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-hal"
import {CreateDataArgs, ImageOpHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal"
import {Box2Like, Matrix3x2, Vector2, Vector2Like} from "@cm/math"
import {definition} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/rasterize-geometry/definition"
import {clipPolygonToRect} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/rasterize-geometry/helpers"
import {JobNodes} from "@cm/job-nodes"
import {RegionLockedHalImageRef} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/region-locked-hal-image-ref"
import {TextureEditorSettings} from "@app/textures/texture-editor/texture-editor-settings"
import {getHalAddressMode} from "@cm/webgl2"
import {RegionMapping} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/region-mapping-abstraction-experiments/region-mapping/region-mapping"
import {mapRegionToMipLevel} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/region-mapping-abstraction-experiments/region-helpers"
import {HalGeometryRef} from "@cm/hal/hal-geometry/index"
import {HalPainterPrimitiveRef} from "@cm/hal/hal-ref"
import {ImageOpHalParameters, UpdateImageOptions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-types"
import {AnyImageOpHalData, CtorArgs, ImageOpHalData} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-data"
import {UPDATE_COMPLEXITY_MID} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/update-complexity"

const MIN_MIP_LEVEL_BIAS = 0 // don't allow negative mip level bias as it can blow up memory usage
const MAX_MIP_LEVEL_BIAS = Number.POSITIVE_INFINITY
const USE_CONSERVATIVE_RASTERIZATION = true
const MIN_TEXTURE_MIP_LEVEL_HEAD_ROOM = 8 // to avoid bleeding from higher mip levels, we need to restrict the maximum mip-level of the texture

type Definition = typeof definition

export class ImageOpRasterizeGeometryHal extends ImageOpHal<Definition, ImageOpRasterizeGeometryHalData> {
    constructor(ctx: ImageOpContextHal, parameters: ImageOpHalParameters<Definition>) {
        super(ctx)
        this.setParameters(parameters)
    }

    protected override createData(args: CreateDataArgs<Definition, ImageOpRasterizeGeometryHalData>): ImageOpRasterizeGeometryHalData {
        return new ImageOpRasterizeGeometryHalData({
            ...args,
            descriptor: args.parameters.descriptor,
            outputImageReuse: "reuse-if-same-descriptor",
            updateComplexity: UPDATE_COMPLEXITY_MID,
        })
    }
}

class ImageOpRasterizeGeometryHalData extends ImageOpHalData<Definition> {
    constructor(args: CtorArgs<Definition, ImageOpRasterizeGeometryHalData>) {
        super(args)
        this.geometry = this.ctx.createGeometry()
        this.painter = args.prevData?.painter.addRef() ?? this.createPainter()

        // TODO only update geometry if it changed
        // update geometry
        if (JobNodes.isDataObjectReference(this.parameters.geometry)) {
            throw new Error("DataObjectReference is not supported in WebGL2")
        }
        const numVertices = this.parameters.geometry.vertices.positions.length / 2
        const vertexPositions = this.parameters.geometry.vertices.positions
        const vertexUVs = this.parameters.geometry.vertices.uvs
        const vertexColors = this.parameters.geometry.vertices.colors
        const indices = this.parameters.geometry.indices ?? new Uint32Array(numVertices).map((_, index) => index)
        this.geometry.ref.clear()
        this.geometry.ref.conservativeRasterization = USE_CONSERVATIVE_RASTERIZATION // we need conservative rasterization to ensure that the geometry is rasterized correctly for higher mip levels
        this.geometry.ref.addVertices(vertexPositions, vertexUVs, vertexColors)
        this.geometry.ref.addIndices(indices)

        this.sourceImageMipLevelBias =
            this.parameters.textureImage && vertexUVs
                ? this.computeSourceImageMipLevelBias(this.parameters.textureImage, vertexPositions, vertexUVs, indices)
                : 0
    }

    protected override dispose() {
        this.painter.release()
        this.geometry.release()
        super.dispose()
    }

    protected override async writeRegion(outputImage: HalImage, mipLevelRegion: MipLevelRegion, options?: UpdateImageOptions): Promise<void> {
        const {textureImage, addressMode} = this.parameters
        textureImage?.addRef("RasterizeGeometry.writeRegion - textureImage")
        let sourceHalImage: RegionLockedHalImageRef | undefined = undefined
        try {
            // update source image
            let sourceMipLevel = 0
            if (textureImage) {
                sourceMipLevel = Math.max(
                    0,
                    Math.min(textureImage.numMipLevels - 1 - MIN_TEXTURE_MIP_LEVEL_HEAD_ROOM, mipLevelRegion.mipLevel + this.sourceImageMipLevelBias),
                )
                const sourceRegion = this.determineSourceRegion(textureImage, mipLevelRegion, sourceMipLevel)
                if (TextureEditorSettings.EnableTests) {
                    const newSourceMipLevelRegions = this.sourceMapping.getSourceMipLevelRegions({
                        targetMipLevelRegionToWrite: mipLevelRegion,
                        sourceMipLevel: sourceMipLevel,
                    })
                    const invalidatedTargetRegions = this.sourceMapping.computeTargetInvalidations(
                        newSourceMipLevelRegions.map((mipLevelRegion) => mapRegionToMipLevel(mipLevelRegion, mipLevelRegion.mipLevel, 0)),
                    )
                    ImageOpRasterizeGeometryHalData.logger?.debug("RasterizeGeometryHal writeRegion")
                    ImageOpRasterizeGeometryHalData.logger?.debug("Original mipLevelRegion", mipLevelRegion)
                    ImageOpRasterizeGeometryHalData.logger?.debug("Original source regions:", sourceRegion)
                    ImageOpRasterizeGeometryHalData.logger?.debug("New source regions: ", newSourceMipLevelRegions)
                    ImageOpRasterizeGeometryHalData.logger?.debug("Invalidated target regions: ", invalidatedTargetRegions)
                }
                sourceHalImage = await textureImage.getUpdatedHalImage(
                    {
                        ...sourceRegion,
                        mipLevel: sourceMipLevel,
                    },
                    options,
                )
            }

            const mipLevelScale = 1 << mipLevelRegion.mipLevel
            // const worldTransform = worldTransform ? Matrix3x2.fromMatrix3x2Like(worldTransform) : Matrix3x2.identity()
            const transform = new Matrix3x2().scale(new Vector2(1 / mipLevelScale)) //.append(worldTransform)
            this.painter.ref.paint({
                target: outputImage,
                geometry: this.geometry.ref,
                sourceImages: sourceHalImage?.halImage,
                parameters: {
                    u_useTexture: {type: "int", value: textureImage ? 1 : 0},
                    u_sourceMipLevel: {type: "int", value: sourceMipLevel},
                    u_addressMode: {type: "int", value: getHalAddressMode(addressMode)},
                },
                options: {
                    clipRegion: mipLevelRegion,
                    targetMipLevel: mipLevelRegion.mipLevel,
                    transform,
                    conservativeRasterizationAddressMode: addressMode,
                },
            })
        } finally {
            sourceHalImage?.release()
            textureImage?.release("RasterizeGeometry.writeRegion - textureImage")
        }
    }

    private computeSourceImageMipLevelBias(
        sourceImage: AnyImageOpHalData,
        vertexPositions: Float32Array,
        vertexUVs: Float32Array,
        indices: Uint32Array,
    ): number {
        const computeMaxTextureStretch = (p: Vector2Like[], uv: Vector2Like[]): number => {
            let maxScaling = 0
            for (let i = 0; i < p.length; i++) {
                const i1 = (i + 1) % p.length
                const p0 = p[i]
                const p1 = p[i1]
                const uv0 = uv[i]
                const uv1 = uv[i1]
                const d = Vector2.distance(uv0, uv1)
                if (d === 0) {
                    continue
                }
                const scaling = Vector2.distance(p0, p1) / d
                if (scaling > maxScaling) {
                    maxScaling = scaling
                }
            }
            return maxScaling
        }

        let maxStretch = 0
        for (let i = 0; i < indices.length; i += 3) {
            const i1 = indices[i]
            const i2 = indices[i + 1]
            const i3 = indices[i + 2]

            const p1 = new Vector2(vertexPositions[i1 * 2 + 0], vertexPositions[i1 * 2 + 1])
            const p2 = new Vector2(vertexPositions[i2 * 2 + 0], vertexPositions[i2 * 2 + 1])
            const p3 = new Vector2(vertexPositions[i3 * 2 + 0], vertexPositions[i3 * 2 + 1])

            const uv1 = new Vector2(vertexUVs[i1 * 2 + 0], vertexUVs[i1 * 2 + 1])
            const uv2 = new Vector2(vertexUVs[i2 * 2 + 0], vertexUVs[i2 * 2 + 1])
            const uv3 = new Vector2(vertexUVs[i3 * 2 + 0], vertexUVs[i3 * 2 + 1])

            maxStretch = Math.max(maxStretch, computeMaxTextureStretch([p1, p2, p3], [uv1, uv2, uv3]))
        }

        const magnificationTolerance = 0.05 // allow 5% stretch without mip level change
        maxStretch /= 1 + magnificationTolerance
        let mipLevelBias = Math.floor(Math.log2(1 / maxStretch))
        mipLevelBias = Math.max(MIN_MIP_LEVEL_BIAS, Math.min(MAX_MIP_LEVEL_BIAS, mipLevelBias))
        return mipLevelBias
    }

    private determineSourceRegion(sourceImage: AnyImageOpHalData, mipLevelRegion: MipLevelRegion, sourceMipLevel: number): Region {
        if (JobNodes.isDataObjectReference(this.parameters.geometry)) {
            throw new Error("DataObjectReference is not supported in WebGL2")
        }
        const vertexPositions = this.parameters.geometry.vertices.positions
        const vertexUVs = this.parameters.geometry.vertices.uvs
        if (!vertexUVs) {
            throw new Error("UVs are required for rasterizing geometry with an image")
        }
        const indices = this.parameters.geometry.indices ?? vertexPositions.map((_, index) => index)

        // scale region to mip level 0
        const mipLevelScale = 1 << mipLevelRegion.mipLevel
        const scaledRegion: Box2Like = {
            x: mipLevelRegion.x * mipLevelScale,
            y: mipLevelRegion.y * mipLevelScale,
            width: mipLevelRegion.width * mipLevelScale,
            height: mipLevelRegion.height * mipLevelScale,
        }
        const rx1 = scaledRegion.x
        const ry1 = scaledRegion.y
        const rx2 = scaledRegion.x + scaledRegion.width
        const ry2 = scaledRegion.y + scaledRegion.height
        // we use the bounds of the UVs of every triangle which intersects the region
        const sourceMipLevelScale = 1 << sourceMipLevel
        let minX = Number.POSITIVE_INFINITY
        let minY = Number.POSITIVE_INFINITY
        let maxX = Number.NEGATIVE_INFINITY
        let maxY = Number.NEGATIVE_INFINITY
        for (let i = 0; i < indices.length; i += 3) {
            const i1 = indices[i]
            const i2 = indices[i + 1]
            const i3 = indices[i + 2]
            const polyPos = [
                new Vector2(vertexPositions[i1 * 2 + 0], vertexPositions[i1 * 2 + 1]),
                new Vector2(vertexPositions[i2 * 2 + 0], vertexPositions[i2 * 2 + 1]),
                new Vector2(vertexPositions[i3 * 2 + 0], vertexPositions[i3 * 2 + 1]),
            ]
            const polyUv = [
                new Vector2(vertexUVs[i1 * 2 + 0], vertexUVs[i1 * 2 + 1]),
                new Vector2(vertexUVs[i2 * 2 + 0], vertexUVs[i2 * 2 + 1]),
                new Vector2(vertexUVs[i3 * 2 + 0], vertexUVs[i3 * 2 + 1]),
            ]

            // quick reject: check position-space bounding box
            const minPx = Math.min(polyPos[0].x, polyPos[1].x, polyPos[2].x)
            const minPy = Math.min(polyPos[0].y, polyPos[1].y, polyPos[2].y)
            const maxPx = Math.max(polyPos[0].x, polyPos[1].x, polyPos[2].x)
            const maxPy = Math.max(polyPos[0].y, polyPos[1].y, polyPos[2].y)

            if (maxPx < rx1 || minPx > rx2 || maxPy < ry1 || minPy > ry2) {
                continue // no intersection
            }

            // Clip triangle in position space
            const clipped = clipPolygonToRect(polyPos, polyUv, rx1, ry1, rx2, ry2)
            if (clipped.length === 0) continue

            // Update UV bounds
            for (const v of clipped) {
                if (v.uv.x < minX) minX = v.uv.x
                if (v.uv.y < minY) minY = v.uv.y
                if (v.uv.x > maxX) maxX = v.uv.x
                if (v.uv.y > maxY) maxY = v.uv.y
            }
        }
        const margin = 1 + (USE_CONSERVATIVE_RASTERIZATION ? 1 : 0) // add margin for interpolation and conservative rasterization
        minX = Math.floor(minX / sourceMipLevelScale) - margin
        maxX = Math.ceil(maxX / sourceMipLevelScale) + margin
        minY = Math.floor(minY / sourceMipLevelScale) - margin
        maxY = Math.ceil(maxY / sourceMipLevelScale) + margin
        return {
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY,
        }
    }

    override onSourceRegionsInvalidated(sourceImage: AnyImageOpHalData, regions: MipLevelRegion[]): void {
        if (JobNodes.isDataObjectReference(this.parameters.geometry)) {
            throw new Error("DataObjectReference is not supported in WebGL2")
        }
        const vertexPositions = this.parameters.geometry.vertices.positions
        const vertexUVs = this.parameters.geometry.vertices.uvs
        if (!vertexUVs) {
            throw new Error("UVs are required for rasterizing geometry with an image")
        }
        const indices = this.parameters.geometry.indices ?? vertexPositions.map((_, index) => index)

        let minX = Number.POSITIVE_INFINITY
        let minY = Number.POSITIVE_INFINITY
        let maxX = Number.NEGATIVE_INFINITY
        let maxY = Number.NEGATIVE_INFINITY
        for (const region of regions) {
            const rx1 = region.x
            const ry1 = region.y
            const rx2 = region.x + region.width
            const ry2 = region.y + region.height
            // we use the bounds of the UVs of every triangle which intersects the region
            for (let i = 0; i < indices.length; i += 3) {
                const i1 = indices[i]
                const i2 = indices[i + 1]
                const i3 = indices[i + 2]
                // reverse pos/uv because we are going from source to target
                const polyPos = [
                    new Vector2(vertexUVs[i1 * 2 + 0], vertexUVs[i1 * 2 + 1]),
                    new Vector2(vertexUVs[i2 * 2 + 0], vertexUVs[i2 * 2 + 1]),
                    new Vector2(vertexUVs[i3 * 2 + 0], vertexUVs[i3 * 2 + 1]),
                ]
                const polyUv = [
                    new Vector2(vertexPositions[i1 * 2 + 0], vertexPositions[i1 * 2 + 1]),
                    new Vector2(vertexPositions[i2 * 2 + 0], vertexPositions[i2 * 2 + 1]),
                    new Vector2(vertexPositions[i3 * 2 + 0], vertexPositions[i3 * 2 + 1]),
                ]

                // quick reject: check position-space bounding box
                const minPx = Math.min(polyPos[0].x, polyPos[1].x, polyPos[2].x)
                const minPy = Math.min(polyPos[0].y, polyPos[1].y, polyPos[2].y)
                const maxPx = Math.max(polyPos[0].x, polyPos[1].x, polyPos[2].x)
                const maxPy = Math.max(polyPos[0].y, polyPos[1].y, polyPos[2].y)

                if (maxPx < rx1 || minPx > rx2 || maxPy < ry1 || minPy > ry2) {
                    continue // no intersection
                }

                // Clip triangle in position space
                const clipped = clipPolygonToRect(polyPos, polyUv, rx1, ry1, rx2, ry2)
                if (clipped.length === 0) continue

                // Update UV bounds
                for (const v of clipped) {
                    if (v.uv.x < minX) minX = v.uv.x
                    if (v.uv.y < minY) minY = v.uv.y
                    if (v.uv.x > maxX) maxX = v.uv.x
                    if (v.uv.y > maxY) maxY = v.uv.y
                }
            }
        }
        const margin = 1 + (USE_CONSERVATIVE_RASTERIZATION ? 1 : 0) // add margin for interpolation and conservative rasterization
        minX = Math.floor(minX) - margin
        maxX = Math.ceil(maxX) + margin
        minY = Math.floor(minY) - margin
        maxY = Math.ceil(maxY) + margin
        const invalidatedRegion: Region = {
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY,
        }
        this.invalidateRegions([invalidatedRegion])
    }

    private createPainter() {
        return this.ctx.createPainter(
            "primitive",
            `
                uniform int u_useTexture;
                uniform int u_sourceMipLevel;
                uniform int u_addressMode;

                vec4 computeColor(vec2 worldPosition, vec2 uv, vec4 color) {
                    if (u_useTexture != 0) {
                        float scaling = float(1 << u_sourceMipLevel);
                        vec2 sourcePixel = uv / scaling;
                        color *= textureLodPx0(sourcePixel, u_sourceMipLevel, u_addressMode);
                    }
                    return color;
                }
            `,
        )
    }

    private readonly painter: HalPainterPrimitiveRef
    private readonly geometry: HalGeometryRef
    private sourceImageMipLevelBias = 0
    private sourceMapping = new RegionMapping()
}
